/**
 * HaveDataDateRange 组件工具函数
 * 包含趋势数据合并、日期查找、时间范围计算等纯函数
 */
import moment from 'moment';
import { TrendItem } from './type';

/**
 * 合并 keyMetricsData 中各字段的 trend_list
 * @description 遍历数据对象的所有字段，将包含 trend_list 的数据合并为一个扁平数组
 * @param data - keyMetricsData.data 对象，每个字段可能包含 trend_list
 * @returns 合并后的趋势数据数组
 */
export function mergeTrendLists(data: Record<string, any> | undefined): TrendItem[] {
  if (!data) return [];
  return Object.values(data).reduce<TrendItem[]>((acc, item) => {
    if (item?.trend_list) {
      return acc.concat(item.trend_list);
    }
    return acc;
  }, []);
}

/**
 * 从趋势数据末尾向前查找最新有数据的日期
 * @description 倒序遍历 trendList，找到第一个 metrics 中存在非空 value 的条目
 * @param trendList - 趋势数据数组
 * @returns 最新有数据的日期字符串，未找到则返回 null
 */
export function findLatestDataDate(trendList: TrendItem[]): string | null {
  for (let i = trendList.length - 1;i >= 0;i--) {
    const { metrics } = trendList[i];
    const hasData = Object.values(metrics || {}).some(
      (metric) => metric.value !== '',
    );
    if (hasData) {
      return trendList[i].date;
    }
  }
  return null;
}

/**
 * 计算展示的时间范围字符串
 * @description 根据最新有数据日期、筛选器时间范围、时间粒度等信息计算展示文案
 *   - 无数据时展示筛选器默认时间范围
 *   - 周粒度下，若周结束日期超过今天则展示 T-1 日期
 *   - 其他粒度展示最新有数据的日期
 * @param haveDataDate - 最新有数据的日期，null 表示无数据
 * @param rangeTime - 筛选器起止时间数组 [start, end]
 * @param timeType - 时间粒度类型 ('Day' | 'Week' | 'Month')
 * @param timeZone - 时区偏移量
 * @param dateFormat - 日期格式化模板
 * @returns 格式化的时间范围字符串，如 '2025-01-01 ~ 2025-01-31'
 */
export function calcDisplayDateRange(
  haveDataDate: string | null,
  rangeTime: string[],
  timeType: string,
  timeZone: number,
  dateFormat: string,
): string {
  const defaultRange = `${moment(rangeTime[0])?.format(dateFormat)} ~ ${moment(rangeTime[1])?.format(dateFormat)}`;

  if (!haveDataDate) return defaultRange;

  const start = moment(rangeTime[0]).format(dateFormat);
  let end: string;

  if (timeType === 'Week') {
    const today = moment().utcOffset(timeZone).startOf('day');
    const weekEndDate = moment(haveDataDate).add(6, 'days').startOf('day');

    // 周粒度: 结束日期超过今天则展示 T-1 日期
    end = weekEndDate.isAfter(today)
      ? moment().utcOffset(timeZone).subtract(1, 'day').format(dateFormat)
      : weekEndDate.format(dateFormat);
  } else {
    // 其他粒度默认展示最新有数据的日期
    end = moment(haveDataDate).format(dateFormat);
  }

  return `${start} ~ ${end}`;
}
